import { RotateLayout } from "@pdfking/filling-flex"
import type { Degree, Distance } from "@pdfking/math"
import { ClampMargins, MarginsBetween, Outline } from "@pdfking/math"
import { HALF_TURN } from "@pdfking/math/constants"

import { DEFAULT_FILE_BLEED, DYNAMIC_OFFSET, MEASURE_ARROW_SIZE } from "#constants"
import type { FinalHook, PackedSheet } from "#internal"

import { HELP_LAYER_ID } from "../../../data/LayerList"
import { AddDrawing } from "../../common/layout/AddDrawing"
import { LeafNodes } from "../../common/layout/LeafNodes"
import { LeafPages } from "../../common/layout/LeafPages"
import { ResolveSheet } from "../pipeline/PipelineSheets"
import { GetShapesForUserHelp } from "./GetShapesForUserHelp"

// A sheet whose pages lie on their side is stood back up, so they read the
// way they print. A half turn is left alone: it is the pages head to head, or
// the sheet turned on purpose.
const upright = (sheet: PackedSheet): PackedSheet => {
  const [first] = LeafNodes(ResolveSheet(sheet).root)
  return first && first.rotation % HALF_TURN
    ? { ...sheet, layout: RotateLayout(sheet.layout, -first.rotation as Degree) }
    : sheet
}

// How far past its pages a grow's measures reach (`DrawGrowHelp`).
const MEASURES = (DYNAMIC_OFFSET + DEFAULT_FILE_BLEED + MEASURE_ARROW_SIZE) as Distance

/**
 * A help sheet's page is what its pages cover: the box round every page laid
 * out on it. The rest of the sheet — padding, margins — is left to bleed, at
 * least as far as the measures reach, and the legend stands on the page.
 */
export const HelpJobProcessor: FinalHook = (_ctx, entries) =>
  entries.map(upright).map(sheet => {
    const { root } = ResolveSheet(sheet)
    const pages = [...LeafPages(root)].map(page => page.position)
    const position = pages.length ? Outline(pages) : root.position
    const layout = AddDrawing(
      sheet.layout,
      HELP_LAYER_ID,
      (_node, frame) => GetShapesForUserHelp(frame.from_sheet(position)),
      true
    )
    const info = {
      ...sheet.info,
      position,
      bleed: ClampMargins(MarginsBetween(root.position, position), MEASURES),
    }
    return { ...sheet, layout, info }
  })
